// RevenueForecastReport.jsx - Projected revenue based on remaining auth hours
import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { auditApi } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const RevenueForecastReport = () => {
  const { user } = useContext(AuthContext);

  const tenant_id = user?.tenant_id;

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    customer_id: "",
    group_by: "customer", // customer, consumer, service, authorization
  });
  const [customers, setCustomers] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Toggle row expansion
  const toggleRowExpansion = (index) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Expand/collapse all rows
  const toggleAllRows = () => {
    if (expandedRows.size === reportData.length) {
      setExpandedRows(new Set());
    } else {
      setExpandedRows(new Set(reportData.map((_, i) => i)));
    }
  };

  // Load filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const customerRes = await secureAxios.get("/api/customers", {
          params: { tenant_id },
        });
        setCustomers(customerRes.data || []);
      } catch (err) {
        console.error("Error loading filter options:", err);
      }
    };
    if (tenant_id) loadFilterOptions();
  }, [tenant_id]);

  // Load report data
  const loadReport = useCallback(async () => {
    if (!tenant_id) return;
    setLoading(true);
    try {
      const response = await secureAxios.get(
        "/api/business-reports/revenue-forecast",
        {
          params: {
            tenant_id,
            customer_id: filters.customer_id || undefined,
            group_by: filters.group_by,
          },
        }
      );

      if (response.data.success) {
        setReportData(response.data.data || []);
        setSummary(response.data.summary || null);
        setExpandedRows(new Set()); // Reset expanded rows when data changes

        await auditApi.logEntry({
          action: "GENERATE_REVENUE_FORECAST_REPORT",
          entity_type: "REPORT",
          entity_id: null,
          contains_phi: true,
          action_status: "SUCCESS",
          purpose_of_use: "OPERATIONS",
        });
      }
    } catch (err) {
      console.error("Error loading revenue forecast report:", err);
      toast.error("Failed to load revenue forecast report");
    } finally {
      setLoading(false);
    }
  }, [tenant_id, filters]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  const formatNumber = (num, decimals = 1) => {
    return Number(num || 0).toFixed(decimals);
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-graph-up-arrow me-2"></i>
            Revenue Forecast Report
          </h2>
          <p className="text-muted mb-0">
            Revenue pipeline: unbilled hours ready to invoice + remaining authorization potential
          </p>
        </div>
        <button
          className="btn btn-outline-primary"
          onClick={loadReport}
          disabled={loading}
        >
          <i className="bi bi-arrow-clockwise me-1"></i>
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-4">
              <label className="form-label">Group By</label>
              <select
                className="form-select"
                value={filters.group_by}
                onChange={(e) =>
                  setFilters({ ...filters, group_by: e.target.value })
                }
              >
                <option value="customer">By Customer</option>
                <option value="consumer">By Consumer</option>
                <option value="service">By Service</option>
                <option value="authorization">By Authorization</option>
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Customer</label>
              <select
                className="form-select"
                value={filters.customer_id}
                onChange={(e) =>
                  setFilters({ ...filters, customer_id: e.target.value })
                }
              >
                <option value="">All Customers</option>
                {customers.map((c) => (
                  <option key={c.customer_id} value={c.customer_id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="row g-3 mb-4">
          <div className="col-6 col-lg">
            <div className="card bg-primary text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Total Auth Hours/Mileages
                </h6>
                <h4 className="card-title mb-0">
                  {formatNumber(summary.total_auth_hours, 0)}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-lg">
            <div className="card bg-info text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Hours Used
                </h6>
                <h4 className="card-title mb-0">
                  {formatNumber(summary.hours_used, 0)}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-lg">
            <div className="card bg-danger text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Unbilled Hours
                </h6>
                <h4 className="card-title mb-0">
                  {formatNumber(summary.hours_unbilled, 0)}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-lg">
            <div className="card bg-success text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Hours Remaining
                </h6>
                <h4 className="card-title mb-0">
                  {formatNumber(summary.hours_remaining, 0)}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-lg">
            <div className="card border-danger h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 text-muted small">
                  Unbilled Revenue
                </h6>
                <h4 className="card-title mb-0 text-danger">
                  {formatCurrency(summary.unbilled_revenue)}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-lg">
            <div className="card bg-warning text-dark h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Projected Revenue
                </h6>
                <h4 className="card-title mb-0">
                  {formatCurrency(summary.projected_revenue)}
                </h4>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Data Table */}
      {!loading && reportData.length > 0 && (
        <div className="card">
          {/* Expand/Collapse All button - only show when not grouping by authorization */}
          {filters.group_by !== "authorization" && (
            <div className="card-header bg-light py-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={toggleAllRows}
              >
                <i
                  className={`bi bi-${
                    expandedRows.size === reportData.length
                      ? "dash-square"
                      : "plus-square"
                  } me-1`}
                ></i>
                {expandedRows.size === reportData.length
                  ? "Collapse All"
                  : "Expand All"}
              </button>
            </div>
          )}
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-dark">
                <tr>
                  {filters.group_by !== "authorization" && (
                    <th style={{ width: "40px" }}></th>
                  )}
                  <th>Name</th>
                  <th>Details</th>
                  <th className="text-end">Auth Hours/Mileages</th>
                  <th className="text-end">Used</th>
                  <th className="text-end text-danger">Unbilled</th>
                  <th className="text-end">Remaining</th>
                  <th className="text-end">Rate</th>
                  <th className="text-end text-danger">Unbilled Rev</th>
                  <th className="text-end">Projected Revenue</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((row, index) => {
                  const isExpanded = expandedRows.has(index);
                  const hasDetails = row.details && row.details.length > 0;

                  return (
                    <React.Fragment key={index}>
                      {/* Main Row */}
                      <tr
                        className={hasDetails ? "cursor-pointer" : ""}
                        onClick={() => hasDetails && toggleRowExpansion(index)}
                        style={hasDetails ? { cursor: "pointer" } : {}}
                      >
                        {filters.group_by !== "authorization" && (
                          <td className="text-center">
                            {hasDetails && (
                              <i
                                className={`bi bi-chevron-${
                                  isExpanded ? "down" : "right"
                                } text-muted`}
                              ></i>
                            )}
                          </td>
                        )}
                        <td className="fw-bold">{row.primary_name}</td>
                        <td className="text-muted small">
                          {row.secondary_name}
                        </td>
                        <td className="text-end">
                          {formatNumber(row.auth_hours, 0)}
                        </td>
                        <td className="text-end">
                          {formatNumber(row.hours_used, 1)}
                        </td>
                        <td className="text-end fw-bold text-danger">
                          {formatNumber(row.hours_unbilled, 1)}
                        </td>
                        <td className="text-end fw-bold text-success">
                          {formatNumber(row.hours_remaining, 1)}
                        </td>
                        <td className="text-end">
                          {row.rate === "Varies" ? (
                            <span className="badge bg-secondary">Varies</span>
                          ) : (
                            formatCurrency(row.rate)
                          )}
                        </td>
                        <td className="text-end fw-bold text-danger">
                          {formatCurrency(row.unbilled_revenue)}
                        </td>
                        <td className="text-end fw-bold text-primary">
                          {formatCurrency(row.projected_revenue)}
                        </td>
                      </tr>

                      {/* Detail Rows */}
                      {isExpanded &&
                        hasDetails &&
                        row.details.map((detail, detailIndex) => (
                          <tr
                            key={`${index}-${detailIndex}`}
                            className="bg-light"
                          >
                            <td></td>
                            <td className="ps-4 text-muted small">
                              <i className="bi bi-arrow-return-right me-2"></i>
                              {detail.authorization_number}
                            </td>
                            <td className="text-muted small">
                              {filters.group_by === "customer" &&
                                detail.consumer_name}
                              {filters.group_by === "consumer" &&
                                detail.service_name}
                              {filters.group_by === "service" &&
                                detail.consumer_name}
                              {filters.group_by !== "customer" &&
                                filters.group_by !== "consumer" &&
                                filters.group_by !== "service" &&
                                detail.service_name}
                            </td>
                            <td className="text-end text-muted small">
                              {formatNumber(detail.auth_hours, 0)}
                            </td>
                            <td className="text-end text-muted small">
                              {formatNumber(detail.hours_used, 1)}
                            </td>
                            <td className="text-end text-danger small">
                              {formatNumber(detail.hours_unbilled, 1)}
                            </td>
                            <td className="text-end text-muted small">
                              {formatNumber(detail.hours_remaining, 1)}
                            </td>
                            <td className="text-end text-muted small">
                              {formatCurrency(detail.rate)}
                            </td>
                            <td className="text-end text-danger small">
                              {formatCurrency(detail.unbilled_revenue)}
                            </td>
                            <td className="text-end text-muted small">
                              {formatCurrency(detail.projected_revenue)}
                            </td>
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot className="table-secondary">
                <tr className="fw-bold">
                  <td colSpan={filters.group_by !== "authorization" ? 3 : 2}>
                    Totals
                  </td>
                  <td className="text-end">
                    {formatNumber(summary?.total_auth_hours, 0)}
                  </td>
                  <td className="text-end">
                    {formatNumber(summary?.hours_used, 1)}
                  </td>
                  <td className="text-end text-danger">
                    {formatNumber(summary?.hours_unbilled, 1)}
                  </td>
                  <td className="text-end text-success">
                    {formatNumber(summary?.hours_remaining, 1)}
                  </td>
                  <td></td>
                  <td className="text-end text-danger">
                    {formatCurrency(summary?.unbilled_revenue)}
                  </td>
                  <td className="text-end text-primary">
                    {formatCurrency(summary?.projected_revenue)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* No Data */}
      {!loading && reportData.length === 0 && (
        <div className="text-center py-5">
          <i
            className="bi bi-inbox text-muted"
            style={{ fontSize: "3rem" }}
          ></i>
          <h5 className="mt-3">No Data Available</h5>
          <p className="text-muted">
            No authorizations found matching your criteria.
          </p>
        </div>
      )}
    </div>
  );
};

export default RevenueForecastReport;